import * as React from "react";
import MDEditor from "@uiw/react-md-editor";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api } from "@/lib/api";
import type { EmployeeTemplate, LlmToolSummary, ToolsPolicy } from "@/types/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { cn, formatDateTime } from "@/lib/utils";

type Channel = "default" | "chat" | "voice" | "email";
const CHANNELS = [
  { id: "default", labelKey: "editor.channels.default" },
  { id: "chat", labelKey: "editor.channels.chat" },
  { id: "voice", labelKey: "editor.channels.voice" },
  { id: "email", labelKey: "editor.channels.email" },
] as const satisfies readonly { id: Channel; labelKey: string }[];

function getChannelContent(t: EmployeeTemplate | undefined, c: Channel): string {
  if (!t) return "";
  if (c === "default") return t.promptGenerator || "";
  return t.channelPrompts?.[c] ?? "";
}

export function EmployeeTemplatePromptPage() {
  const { id = "" } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const { t } = useTranslation("templates");
  const [channel, setChannel] = React.useState<Channel>("default");
  const channelLabel = t(
    CHANNELS.find((c) => c.id === channel)?.labelKey ?? "editor.channels.default"
  );

  const { data, isLoading, error } = useQuery({
    queryKey: ["employee-template", id],
    queryFn: () => api.get<{ template: EmployeeTemplate }>(`/api/admin/employee-templates/${id}`),
    enabled: Boolean(id),
  });

  const toolsQuery = useQuery({
    queryKey: ["llm-tools-summaries"],
    queryFn: () => api.get<{ tools: LlmToolSummary[] }>("/api/admin/llm-tools/summaries"),
  });

  const [value, setValue] = React.useState<string>("");
  const [allowedTools, setAllowedTools] = React.useState<Set<string>>(new Set());
  const [initialized, setInitialized] = React.useState(false);

  React.useEffect(() => {
    if (data?.template && !initialized) {
      setValue(getChannelContent(data.template, channel));
      const policy = (data.template.toolsPolicy ?? {}) as ToolsPolicy;
      setAllowedTools(new Set(policy.allowed ?? []));
      setInitialized(true);
    }
  }, [data, initialized, channel]);

  // Switching channel reloads the editor content from the latest server data.
  React.useEffect(() => {
    if (data?.template && initialized) {
      setValue(getChannelContent(data.template, channel));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel, data?.template?.promptGenerator, data?.template?.channelPrompts]);

  const promptMutation = useMutation({
    mutationFn: (prompt: string) =>
      api.put<{ template: EmployeeTemplate }>(
        `/api/admin/employee-templates/${id}/prompt`,
        channel === "default"
          ? { promptGenerator: prompt }
          : { channel, promptGenerator: prompt }
      ),
    onSuccess: (res) => {
      toast.success(
        channel === "default"
          ? t("editor.toast.defaultSaved")
          : t("editor.toast.channelSaved", { channel: channelLabel })
      );
      qc.setQueryData(["employee-template", id], res);
      qc.invalidateQueries({ queryKey: ["employee-templates"] });
    },
    onError: (err) => {
      toast.error(
        t("errors.saveFailed", { message: err instanceof Error ? err.message : String(err) })
      );
    },
  });

  const clearOverrideMutation = useMutation({
    mutationFn: () =>
      api.put<{ template: EmployeeTemplate }>(
        `/api/admin/employee-templates/${id}/prompt`,
        { channel, promptGenerator: "" }
      ),
    onSuccess: (res) => {
      toast.success(t("editor.toast.overrideRemoved", { channel: channelLabel }));
      qc.setQueryData(["employee-template", id], res);
      qc.invalidateQueries({ queryKey: ["employee-templates"] });
      setValue("");
    },
    onError: (err) => {
      toast.error(
        t("editor.toast.clearOverrideFailed", {
          message: err instanceof Error ? err.message : String(err),
        })
      );
    },
  });

  const toolsMutation = useMutation({
    mutationFn: (next: Set<string>) => {
      const existing = (data?.template?.toolsPolicy ?? {}) as ToolsPolicy;
      const nextPolicy: ToolsPolicy = {
        ...existing,
        allowed: Array.from(next).sort(),
      };
      return api.put<{ template: EmployeeTemplate }>(
        `/api/admin/employee-templates/${id}/tools`,
        { toolsPolicy: nextPolicy }
      );
    },
    onMutate: (next) => {
      const rollback = new Set(allowedTools);
      setAllowedTools(next);
      return { rollback };
    },
    onSuccess: (res) => {
      qc.setQueryData(["employee-template", id], res);
      qc.invalidateQueries({ queryKey: ["employee-templates"] });
    },
    onError: (err, _next, ctx) => {
      if (ctx?.rollback) setAllowedTools(ctx.rollback);
      toast.error(
        t("errors.saveFailed", { message: err instanceof Error ? err.message : String(err) })
      );
    },
  });

  const toolsByCategory = React.useMemo(() => {
    const map: Record<string, LlmToolSummary[]> = {};
    for (const tool of toolsQuery.data?.tools ?? []) {
      if (!map[tool.category]) map[tool.category] = [];
      map[tool.category].push(tool);
    }
    return map;
  }, [toolsQuery.data]);

  function toggleTool(toolId: string) {
    const next = new Set(allowedTools);
    if (next.has(toolId)) next.delete(toolId);
    else next.add(toolId);
    toolsMutation.mutate(next);
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Link to="/templates" className="hover:underline">{t("list.title")}</Link>
        <span>/</span>
        <span>{data?.template?.name ?? t("editor.fallbackBreadcrumb")}</span>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{data?.template?.name ?? t("editor.fallbackTitle")}</CardTitle>
          <CardDescription>
            <Trans t={t} i18nKey="editor.description" components={{ code: <code /> }} />
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-96 w-full" />
          ) : error ? (
            <p className="text-sm text-destructive">
              {t("errors.loadFailed", {
                message: error instanceof Error ? error.message : String(error),
              })}
            </p>
          ) : data?.template ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 rounded-md border bg-muted/40 p-3 text-sm md:grid-cols-3">
                <Meta k={t("editor.meta.templateId")} v={data.template.templateId} />
                <Meta k={t("editor.meta.type")} v={data.template.employeeType} />
                <Meta k={t("editor.meta.promptVersion")} v={data.template.promptGeneratorVersion || "—"} />
                <Meta
                  k={t("editor.meta.status")}
                  v={<Badge variant={data.template.isActive ? "success" : "muted"}>{data.template.isActive ? t("status.active") : t("status.inactive")}</Badge>}
                />
                <Meta k={t("editor.meta.lastUpdated")} v={formatDateTime(data.template.updatedAt)} />
                <Meta k={t("editor.meta.updatedBy")} v={data.template.updatedBy ?? "—"} />
              </div>

              {/* Channel switcher — Default vs per-channel overrides. */}
              <div className="flex flex-wrap items-center gap-2">
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t("editor.channel")}
                </div>
                <div className="flex flex-wrap gap-1 rounded-md border p-1">
                  {CHANNELS.map((c) => {
                    const isOverridden =
                      c.id !== "default" &&
                      Boolean(data.template.channelPrompts?.[c.id]);
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => setChannel(c.id)}
                        className={cn(
                          "inline-flex items-center gap-1.5 rounded px-3 py-1 text-sm font-medium transition-colors",
                          channel === c.id
                            ? "bg-primary text-primary-foreground"
                            : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                        )}
                      >
                        <span>{t(c.labelKey)}</span>
                        {isOverridden && <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />}
                      </button>
                    );
                  })}
                </div>
                {channel !== "default" && (
                  <p className="ml-auto text-xs text-muted-foreground">
                    {data.template.channelPrompts?.[channel]
                      ? t("editor.overridesDefault")
                      : t("editor.emptyFallsBack")}
                  </p>
                )}
              </div>

              <div data-color-mode="light">
                <MDEditor
                  value={value}
                  onChange={(v) => setValue(v ?? "")}
                  height={520}
                  key={channel /* re-mount editor when channel changes */}
                />
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => {
                    if (channel === "default" && !value.trim()) {
                      toast.error(t("editor.toast.defaultEmpty"));
                      return;
                    }
                    promptMutation.mutate(value);
                  }}
                  disabled={promptMutation.isPending}
                >
                  {promptMutation.isPending
                    ? t("actions.saving")
                    : t("editor.saveChannelPrompt", { channel: channelLabel })}
                </Button>
                {channel !== "default" && data.template.channelPrompts?.[channel] && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      if (window.confirm(t("editor.confirmClearOverride", { channel: channelLabel }))) {
                        clearOverrideMutation.mutate();
                      }
                    }}
                    disabled={clearOverrideMutation.isPending}
                  >
                    {clearOverrideMutation.isPending ? t("editor.clearing") : t("editor.clearOverride")}
                  </Button>
                )}
                <Button variant="outline" asChild>
                  <Link to="/templates">{t("actions.backToList")}</Link>
                </Button>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {data?.template && (
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between gap-2">
              <div>
                <CardTitle>{t("editor.tools.title")}</CardTitle>
                <CardDescription>
                  {t("editor.tools.description")}{" "}
                  {t("editor.tools.enabledCount", { count: allowedTools.size })}
                </CardDescription>
              </div>
              <span className="text-xs text-muted-foreground">
                {toolsMutation.isPending ? t("actions.saving") : t("actions.saved")}
              </span>
            </div>
          </CardHeader>
          <CardContent>
            {toolsQuery.isLoading ? (
              <Skeleton className="h-32 w-full" />
            ) : toolsQuery.error ? (
              <p className="text-sm text-destructive">
                {t("editor.tools.loadFailed", {
                  message:
                    toolsQuery.error instanceof Error
                      ? toolsQuery.error.message
                      : String(toolsQuery.error),
                })}
              </p>
            ) : (
              <div className="space-y-6">
                {Object.keys(toolsByCategory).sort().map((cat) => (
                  <div key={cat}>
                    <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {cat}
                    </div>
                    <div className="space-y-2">
                      {toolsByCategory[cat].map((tool) => (
                        <label
                          key={tool.id}
                          className="flex cursor-pointer items-start gap-3 rounded-md border p-3 hover:bg-muted/50"
                        >
                          <input
                            type="checkbox"
                            className="mt-0.5 h-4 w-4"
                            checked={allowedTools.has(tool.toolId)}
                            onChange={() => toggleTool(tool.toolId)}
                          />
                          <div className="flex-1">
                            <div className="font-mono text-xs font-semibold">{tool.toolId}</div>
                            <div className="text-sm text-muted-foreground">
                              {tool.description || "—"}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Meta({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {k}
      </div>
      <div className="mt-0.5">{v}</div>
    </div>
  );
}
